// domVehicleTimeHeaderSticky.tsx
// Makes the time header sticky in VehicleCalendar weekly/day view

export const domVehicleTimeHeaderSticky = (
  calendarSpan: string,
  onNavigate?: (vehicleId: string, date: string) => void,
): (() => void) | undefined => {
  let timerId: number | undefined;

  // Store references to modified elements for cleanup
  const modifiedElements: HTMLElement[] = [];
  const originalStyles: Map<HTMLElement, string> = new Map();
  const eventListeners: Array<{ element: HTMLElement; handler: EventListener }> = [];

  if (calendarSpan === "day" || calendarSpan === "week") {
    timerId = window.setTimeout(() => {
      const calendarContainer = document.querySelector(`#vehicleCalendarContainer`);
      if (!calendarContainer) return;

      // Find the time view element
      const timeView = calendarContainer.querySelector('.rbc-time-view');
      if (!timeView) return;

      // Check if we've already processed this (to avoid duplicates)
      if (timeView.querySelector('.rbc-time-header-clone')) return;

      const timeHeader = calendarContainer.querySelector('.rbc-time-header') as HTMLElement;

      // Only proceed if time header exists
      if (timeHeader) {
        // Clone the timeHeader
        const clonedHeader = timeHeader.cloneNode(true) as HTMLElement;

        // Add a class to identify the clone
        clonedHeader.classList.add('rbc-time-header-clone');

        // Hide original names from the original header (store original display values)
        const originalNameCells = timeHeader.querySelectorAll('.rbc-row.rbc-time-header-cell') as NodeListOf<HTMLElement>;
        originalNameCells.forEach(cell => {
          // Store original display value for cleanup
          originalStyles.set(cell, cell.style.display);
          cell.style.display = 'none';
          modifiedElements.push(cell);
        });

        // Remove all day cells from cloned header so that we have names only
        const allDayCells = clonedHeader.querySelectorAll('.rbc-allday-cell') as NodeListOf<HTMLElement>;
        allDayCells.forEach(cell => {
          cell.style.display = 'none';
        });

        // Apply sticky positioning styles
        clonedHeader.style.position = 'sticky';
        clonedHeader.style.top = '0';
        clonedHeader.style.zIndex = '15';

        // Find the .rbc-time-view container
        const timeViewContainer = calendarContainer.querySelector('.rbc-time-view');

        if (timeViewContainer) {
          // Insert the cloned header as the first child
          timeViewContainer.insertBefore(clonedHeader, timeViewContainer.firstChild);

          // Store reference to cloned header for cleanup
          modifiedElements.push(clonedHeader);

          // Add event delegation for plus button clicks on cloned header
          if (onNavigate) {
            const clickHandler = (e: Event) => {
              const target = e.target as HTMLElement;

              // Check if the click was on a plus button or its parent
              const addButton = target.closest('.vehicle-calendar-add-button') as HTMLElement;

              if (addButton) {
                e.stopPropagation();
                e.preventDefault();

                const date = addButton.getAttribute('data-date');
                const vehicleId = addButton.getAttribute('data-vehicle-id');

                if (date && vehicleId) {
                  console.log('Cloned add button clicked for date:', date);
                  onNavigate(vehicleId, date);
                }
              }
            };

            clonedHeader.addEventListener('click', clickHandler);
            eventListeners.push({ element: clonedHeader, handler: clickHandler });
          }
        }
      }
    }, 150);

    // Return cleanup function
    return () => {
      // Clear the timeout if it exists
      if (timerId) {
        clearTimeout(timerId);
      }

      // Remove event listeners
      eventListeners.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
      eventListeners.length = 0;

      // Remove the cloned header if it exists
      const clonedHeader = document.querySelector('.rbc-time-header-clone');
      if (clonedHeader) {
        clonedHeader.remove();
      }

      // Restore original styles for modified elements
      modifiedElements.forEach(element => {
        // Skip if element is the cloned header (already removed)
        if (element.classList?.contains('rbc-time-header-clone')) {
          return;
        }

        const originalDisplay = originalStyles.get(element);
        if (originalDisplay !== undefined) {
          element.style.display = originalDisplay;
        } else {
          // If no original style was stored, remove the display property
          element.style.removeProperty('display');
        }
      });

      // Clear the stored references
      modifiedElements.length = 0;
      originalStyles.clear();
    };
  }

  return undefined;
};
